import React, { useState } from "react";
import { Target, Globe, Plus, Trash2, Download, Link2, Copy as CopyIcon } from "lucide-react";
import api from "../api";
import { T, primaryBtn, inputStyle, labelStyle, downloadBlob } from "../theme";
import { PillButton } from "./shared";

function escapeHtml(str) {
  return String(str).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}
function sanitizeUrl(url) {
  const trimmed = String(url || "").trim();
  if (/^\s*(javascript|data|vbscript):/i.test(trimmed)) return "#";
  return trimmed;
}

export default function ProfileTab({ profile, personas, actions, subTab, setSubTab }) {
  const [showPersonaForm, setShowPersonaForm] = useState(false);
  const [personaForm, setPersonaForm] = useState({ nome: "", ruolo: "", settore: "", dimensione_azienda: "", pain_point: "", tono: "", canale: "" });
  const [headline, setHeadline] = useState(profile.bio_headline || profile.name);
  const [links, setLinks] = useState((profile.bio_links || []).map((l, i) => ({ ...l, _key: i })));
  const [bioSaved, setBioSaved] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [copied, setCopied] = useState(false);

  const publicUrl = profile.bio_slug ? `${process.env.REACT_APP_BACKEND_URL}/api/bio/${profile.bio_slug}` : null;

  async function togglePublish() {
    setPublishing(true);
    try {
      const path = profile.bio_published ? "bio-unpublish" : "bio-publish";
      if (!profile.bio_published) await saveBio(headline, links);
      const { data } = await api.post(`/profiles/${profile.id}/${path}`);
      actions.replaceProfile(data);
    } finally {
      setPublishing(false);
    }
  }

  function copyUrl() {
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }

  async function savePersona() {
    if (!personaForm.nome.trim()) return;
    await actions.createPersona(personaForm);
    setPersonaForm({ nome: "", ruolo: "", settore: "", dimensione_azienda: "", pain_point: "", tono: "", canale: "" });
    setShowPersonaForm(false);
  }

  async function saveBio(nextHeadline, nextLinks) {
    await api.post(`/profiles/${profile.id}/bio-links`, {
      headline: nextHeadline,
      links: nextLinks.filter((l) => l.label || l.url).map((l) => ({ label: l.label || "", url: l.url || "" })),
    });
    setBioSaved(true);
    setTimeout(() => setBioSaved(false), 1800);
    actions.refreshProfiles();
  }

  function exportBioPage() {
    const linksHtml = links.filter((l) => l.label && l.url).map((l) =>
      `<a class="link" href="${escapeHtml(sanitizeUrl(l.url))}" target="_blank" rel="noopener">${escapeHtml(l.label)}</a>`
    ).join("\n");
    const safeTitle = escapeHtml(headline || profile.name);
    const html = `<!DOCTYPE html>
<html lang="it"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1">
<title>${safeTitle}</title>
<style>
  body { margin:0; min-height:100vh; background:${T.bg}; color:${T.text}; font-family:-apple-system,sans-serif; display:flex; justify-content:center; padding:40px 16px; }
  .page { width:100%; max-width:420px; text-align:center; }
  h1 { font-size:20px; margin-bottom:28px; }
  .link { display:block; background:${T.surface}; border:1px solid ${T.line}; color:${T.text}; text-decoration:none; padding:14px; border-radius:10px; margin-bottom:12px; font-size:15px; }
  .link:hover { border-color:${T.amber}; }
  footer { margin-top:24px; font-size:11px; color:${T.textDim}; }
</style></head>
<body><div class="page"><h1>${safeTitle}</h1>${linksHtml}<footer>Realizzato con So.Help</footer></div></body></html>`;
    downloadBlob(`${profile.name}-link-in-bio.html`, html, "text/html");
  }

  return (
    <div data-testid="profile-tab">
      <div style={{ display: "flex", gap: 8, marginBottom: 22 }}>
        <PillButton testId="profile-persona-pill" active={subTab === "cliente"} onClick={() => setSubTab("cliente")} icon={Target}>Cliente ideale</PillButton>
        <PillButton testId="profile-bio-pill" active={subTab === "bio"} onClick={() => setSubTab("bio")} icon={Globe}>Link in bio</PillButton>
      </div>

      {subTab === "cliente" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", alignItems: "center", marginBottom: 16, gap: 10 }}>
            <div style={{ color: T.textDim, fontSize: 13, maxWidth: 480 }}>Descrivi il cliente che vuoi raggiungere: guiderà tono e contenuti di ogni profilo.</div>
            <button data-testid="persona-new-btn" onClick={() => setShowPersonaForm(!showPersonaForm)} style={{ ...primaryBtn, display: "flex", alignItems: "center", gap: 6 }}><Plus size={14} /> Nuovo cliente tipo</button>
          </div>
          {showPersonaForm && (
            <div data-testid="persona-form" style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: 10, padding: 16, marginBottom: 20 }}>
              <div className="grid-3" style={{ marginBottom: 12 }}>
                <div><label style={labelStyle}>Nome / etichetta</label><input data-testid="persona-name-input" style={inputStyle} value={personaForm.nome} onChange={(e) => setPersonaForm({ ...personaForm, nome: e.target.value })} placeholder="es. Laura, Responsabile Acquisti" /></div>
                <div><label style={labelStyle}>Ruolo</label><input data-testid="persona-role-input" style={inputStyle} value={personaForm.ruolo} onChange={(e) => setPersonaForm({ ...personaForm, ruolo: e.target.value })} /></div>
                <div><label style={labelStyle}>Settore</label><input data-testid="persona-sector-input" style={inputStyle} value={personaForm.settore} onChange={(e) => setPersonaForm({ ...personaForm, settore: e.target.value })} /></div>
                <div><label style={labelStyle}>Dimensione azienda</label><input style={inputStyle} value={personaForm.dimensione_azienda} onChange={(e) => setPersonaForm({ ...personaForm, dimensione_azienda: e.target.value })} /></div>
                <div><label style={labelStyle}>Tono di comunicazione</label><input style={inputStyle} value={personaForm.tono} onChange={(e) => setPersonaForm({ ...personaForm, tono: e.target.value })} /></div>
                <div><label style={labelStyle}>Canale e orario preferito</label><input style={inputStyle} value={personaForm.canale} onChange={(e) => setPersonaForm({ ...personaForm, canale: e.target.value })} /></div>
              </div>
              <label style={labelStyle}>Problema principale che vuoi risolvergli</label>
              <textarea data-testid="persona-painpoint-input" style={{ ...inputStyle, marginBottom: 12, minHeight: 60, resize: "vertical" }} value={personaForm.pain_point} onChange={(e) => setPersonaForm({ ...personaForm, pain_point: e.target.value })} />
              <button data-testid="persona-save-btn" onClick={savePersona} style={primaryBtn}>Salva</button>
            </div>
          )}
          {personas.length === 0 && !showPersonaForm && (
            <div data-testid="persona-empty-state" style={{ color: T.textDim, fontSize: 13, background: T.surface, border: `1px dashed ${T.line}`, borderRadius: 10, padding: 24, textAlign: "center" }}>
              Nessun cliente ideale ancora definito. Creane uno con "Nuovo cliente tipo": V.I.K.I. lo userà per scrivere didascalie e strategie più mirate.
            </div>
          )}
          <div className="grid-2" style={{ gap: 14 }}>
            {personas.map((p) => (
              <div key={p.id} data-testid={`persona-card-${p.id}`} style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: 10, padding: 16, position: "relative" }}>
                <button data-testid={`persona-delete-${p.id}`} onClick={() => actions.removePersona(p.id)} style={{ position: "absolute", top: 12, right: 12, background: "transparent", border: "none", color: T.textDim, cursor: "pointer" }}><Trash2 size={14} /></button>
                <div style={{ fontFamily: T.fontHead, fontSize: 15, marginBottom: 10 }}>{p.nome}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 13 }}>
                  <div><span style={{ color: T.textDim }}>Ruolo: </span>{p.ruolo}</div>
                  <div><span style={{ color: T.textDim }}>Settore: </span>{p.settore}</div>
                  <div><span style={{ color: T.textDim }}>Dimensione: </span>{p.dimensione_azienda}</div>
                  <div><span style={{ color: T.textDim }}>Tono: </span>{p.tono}</div>
                  <div><span style={{ color: T.textDim }}>Canale: </span>{p.canale}</div>
                  <div style={{ marginTop: 4, paddingTop: 8, borderTop: `1px solid ${T.line}`, color: T.text }}>{p.pain_point}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {subTab === "bio" && (
        <div className="stack-on-mobile" style={{ gap: 24 }}>
          <div style={{ flex: 1, maxWidth: 460 }}>
            <div style={{ color: T.textDim, fontSize: 13, marginBottom: 16, maxWidth: 480 }}>
              Una mini pagina con tutti i tuoi link, da mettere nel campo "sito web" della bio Instagram — al posto di un solo link, ne puoi condividere quanti vuoi. Se usi già Linktree o simili va benissimo continuare: questa è pensata per chi preferisce non gestire un abbonamento in più, non per sostituire chi è già soddisfatto.
            </div>
            <label style={labelStyle}>Titolo della pagina</label>
            <input data-testid="bio-headline-input" style={{ ...inputStyle, marginBottom: 16 }} value={headline}
              onChange={(e) => setHeadline(e.target.value)} onBlur={() => saveBio(headline, links)} placeholder={profile.name} />

            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
              {links.map((l, i) => (
                <div key={l._key} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input data-testid={`bio-link-label-${i}`} style={inputStyle} value={l.label} onChange={(e) => setLinks(links.map((x) => x._key === l._key ? { ...x, label: e.target.value } : x))} onBlur={() => saveBio(headline, links)} placeholder="Etichetta (es. Prenota una consulenza)" />
                  <input data-testid={`bio-link-url-${i}`} style={inputStyle} value={l.url} onChange={(e) => setLinks(links.map((x) => x._key === l._key ? { ...x, url: e.target.value } : x))} onBlur={() => saveBio(headline, links)} placeholder="https://" />
                  <button data-testid={`bio-link-remove-${i}`} onClick={() => { const next = links.filter((x) => x._key !== l._key); setLinks(next); saveBio(headline, next); }} style={{ background: "transparent", border: "none", color: T.textDim, cursor: "pointer", flexShrink: 0 }}><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <button data-testid="bio-add-link-btn" onClick={() => setLinks([...links, { label: "", url: "", _key: Date.now() }])} style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: `1px dashed ${T.line}`, color: T.textDim, borderRadius: 6, padding: "8px 12px", fontSize: 13, cursor: "pointer" }}><Plus size={14} /> Aggiungi link</button>
              {links.length > 0 && (
                <button data-testid="bio-download-btn" onClick={exportBioPage} style={{ ...primaryBtn, display: "flex", alignItems: "center", gap: 6 }}><Download size={14} /> Scarica la pagina</button>
              )}
              {bioSaved && <span style={{ fontSize: 12, color: T.teal }}>Salvato</span>}
            </div>

            <div data-testid="bio-publish-card" style={{ marginTop: 20, background: profile.bio_published ? `${T.teal}0D` : T.surface, border: `1px solid ${profile.bio_published ? `${T.teal}66` : T.line}`, borderRadius: 12, padding: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <Link2 size={15} color={profile.bio_published ? T.teal : T.textDim} />
                <div style={{ fontFamily: T.fontHead, fontSize: 14 }}>Pagina online</div>
                {profile.bio_published && <span style={{ fontSize: 10, fontFamily: T.fontHead, color: T.ink, background: T.teal, borderRadius: 20, padding: "2px 8px" }}>Pubblicata</span>}
              </div>
              <div style={{ fontSize: 12, color: T.textDim, lineHeight: 1.5, marginBottom: 12 }}>
                {profile.bio_published
                  ? "La tua pagina è online a un indirizzo stabile: mettilo nella bio di Instagram. Ogni modifica ai link qui sopra si aggiorna da sola, senza riscaricare nulla."
                  : "Pubblica la pagina su un indirizzo web stabile gestito da So.Help: lo metti una volta nella bio di Instagram e resta sempre aggiornato."}
              </div>
              {profile.bio_published && publicUrl && (
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
                  <a data-testid="bio-public-url" href={publicUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: T.amber, wordBreak: "break-all" }}>{publicUrl}</a>
                  <button data-testid="bio-copy-url-btn" onClick={copyUrl} style={{ display: "flex", alignItems: "center", gap: 4, background: "transparent", border: `1px solid ${T.line}`, color: T.textDim, borderRadius: 5, padding: "4px 8px", fontSize: 11, cursor: "pointer", flexShrink: 0 }}>
                    <CopyIcon size={11} /> {copied ? "Copiato!" : "Copia"}
                  </button>
                </div>
              )}
              <button data-testid="bio-publish-toggle-btn" onClick={togglePublish} disabled={publishing} style={{
                ...primaryBtn, background: profile.bio_published ? "transparent" : T.teal,
                color: profile.bio_published ? T.textDim : T.ink,
                border: profile.bio_published ? `1px solid ${T.line}` : "none",
                opacity: publishing ? 0.6 : 1,
              }}>{publishing ? "Un momento…" : profile.bio_published ? "Togli dalla rete" : "Pubblica online"}</button>
            </div>
          </div>

          <div style={{ width: 280, flexShrink: 0 }}>
            <div style={{ fontSize: 12, color: T.textDim, marginBottom: 8 }}>Anteprima</div>
            <div data-testid="bio-preview" style={{ background: T.bg, border: `8px solid ${T.text}`, borderRadius: 24, padding: "28px 18px", textAlign: "center" }}>
              <div style={{ fontFamily: T.fontHead, fontSize: 15, marginBottom: 18 }}>{headline || profile.name}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {links.filter((l) => l.label).map((l) => (
                  <div key={l._key} style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: 8, padding: "10px 8px", fontSize: 12 }}>{l.label}</div>
                ))}
                {links.length === 0 && <div style={{ color: T.textDim, fontSize: 11 }}>Aggiungi il primo link per vedere l'anteprima</div>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
